import React from "react";
import PropTypes from "prop-types";
import { useTheme } from "../../theme/ThemeProvider";

const SectionRenderer = ({ section }) => {
  const { isDarkMode } = useTheme();

  const getTextColor = () => {
    return isDarkMode ? "text-gray-300" : "text-gray-700";
  };

  const getHeadingColor = (level) => {
    if (level === "h3") {
      return isDarkMode ? "text-gray-100" : "text-gray-900";
    }
    return isDarkMode ? "text-white" : "text-gray-900";
  };

  const getLinkColor = () => {
    return isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:underline";
  };

  const renderContent = () => {
    switch (section.type) {
      case "heading":
        const HeadingTag = section.level || "h1";
        const headingClasses = 
          section.level === "h1" 
            ? `text-xl font-bold ${getHeadingColor(section.level)}` 
            : section.level === "h2" 
            ? `text-lg font-semibold ${getHeadingColor(section.level)}`
            : section.level === "h3"
            ? `text-lg font-bold mt-4 mb-2 ${getHeadingColor(section.level)}`
            : "";
        
        const paddingTop = section.level === "h1" ? "12pt" : section.level === "h3" ? "0" : "4pt";
        
        return (
          <HeadingTag 
            className={headingClasses}
            style={{
              paddingTop: paddingTop,
              paddingLeft: "5pt",
              textIndent: "0pt",
              textAlign: "left"
            }}
          >
            {section.text}
          </HeadingTag>
        );

      case "paragraph":
        const paragraphClasses = section.semibold 
          ? `mb-4 font-semibold ${getTextColor()}` 
          : `mb-4 ${getTextColor()}`;
        
        return (
          <p
            className={paragraphClasses}
            style={{
              paddingTop: section.paddingTop || "4pt",
              paddingLeft: "5pt",
              textIndent: "0pt",
              lineHeight: "114%",
              textAlign: "left"
            }}
          >
            {section.text}
            {section.boldText && (
              <span className={`font-bold ${isDarkMode ? "text-gray-200" : ""}`}>{section.boldText}</span>
            )}
            {section.closingText && (
              <span className={getTextColor()}>{section.closingText}</span>
            )}
            {section.email && (
              <>
                <a
                  href={`mailto:${section.email}`}
                  className={getLinkColor()}
                  target="_blank"
                >
                  {section.email}
                </a>
                {section.phone && (
                  <>
                    {" or call us at "}
                    <a
                      href={`tel:${section.phoneText}`}
                      className={getLinkColor()}
                    >
                      {section.phone}
                    </a>
                  </>
                )}
              </>
            )}
          </p>
        );

      case "list":
        return (
          <ul className={`pl-6 mb-4 list-disc ${getTextColor()}`}>
            {section.items.map((item, idx) => (
              <li
                key={idx}
                className={idx < section.items.length - 1 ? "mb-2" : ""}
                style={{
                  paddingTop: idx === 0 ? "12pt" : "1pt",
                  paddingLeft: "5pt",
                  textIndent: "0pt",
                  lineHeight: "114%",
                  textAlign: "left"
                }}
              >
                {item.boldPrefix && (
                  <span className={`font-bold ${isDarkMode ? "text-gray-200" : ""}`}>{item.boldPrefix}</span>
                )}
                <span className={getTextColor()}>{item.text}</span>
              </li>
            ))}
          </ul>
        );

      case "contact":
        return (
          <div className={`flex flex-col gap-2 pl-5 ${getTextColor()}`}>
            <div>
              <a
                href={`mailto:${section.email}`}
                className={getLinkColor()}
                target="_blank"
              >
                Email -{" "}
              </a>
              <a
                href={`mailto:${section.email}`}
                className={getLinkColor()}
                target="_blank"
              >
                {section.email}
              </a>
            </div>
            <div className={getTextColor()}>
              Mobile - <a href={`tel:${section.mobile}`} className={getLinkColor()}>{section.mobile}</a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return renderContent();
};

SectionRenderer.propTypes = {
  section: PropTypes.object.isRequired
};

export default SectionRenderer;